import { message } from 'antd';
import { t } from '../../../../i18n';
import {
    WaypointTypeEnum,
    TmsRouteTypeEnum,
} from '../../../../enums/TmsEnum';
import { isExternalStationRoute } from '../utils/externalStation';
import { RouteWaypointInput } from '../graphql/mutations/routeMutations';
import type { StationRow, RouteFormFilters } from '../types/routeFormTypes';

// 途经点最大数量
const WAYPOINT_MAX_COUNT = 3;

interface UseRouteFormValidationParams {
    filters: RouteFormFilters;
    routeCode: string | undefined;
    showWaypointSection: boolean;
    tableData: StationRow[];
    resolveExtWaypointIdFromRow: (row: StationRow) => number | undefined;
}

export const useRouteFormValidation = ({
    filters,
    routeCode,
    showWaypointSection,
    tableData,
    resolveExtWaypointIdFromRow,
}: UseRouteFormValidationParams) => {

    /**
     * 途经点重复校验
     */
    const validateWaypointDuplicates = (
        waypoints: RouteWaypointInput[],
        startWarehouseId: number,
        endWarehouseId: number,
        isExternalStationRoute: boolean,
        destExtWaypointId?: number | null
    ): boolean => {
        const waypointIds = waypoints
            .filter(w => w.waypointType === WaypointTypeEnum.Waypoint)
            .map(w => w.waypointId);

        if (!isExternalStationRoute) {
            // 如果没有途经点，起点和终点不能相同
            if (waypointIds.length === 0 && startWarehouseId === endWarehouseId) {
                message.error(t('tms.route.messages.duplicateWaypointExists'));
                return false;
            }
            // 检查中间点是否有重复
            const uniqueWaypointIds = new Set(waypointIds);
            if (waypointIds.length !== uniqueWaypointIds.size) {
                message.error(t('tms.route.messages.duplicateWaypointExists'));
                return false;
            }
            // 检查中间点是否与起点或终点相同
            if (startWarehouseId && waypointIds.includes(startWarehouseId)) {
                message.error(t('tms.route.messages.duplicateWaypointExists'));
                return false;
            }
            if (waypointIds.includes(endWarehouseId)) {
                message.error(t('tms.route.messages.duplicateWaypointExists'));
                return false;
            }
        } else {
            // Temu：按 extWaypointId 校验，途经点配置 id 唯一且不能与终点相同
            const waypointExtIds = waypoints
                .filter(w => w.waypointType === WaypointTypeEnum.Waypoint)
                .map(w => w.extWaypointId)
                .filter((id): id is number => id != null);
            if (new Set(waypointExtIds).size !== waypointExtIds.length) {
                message.error(t('tms.route.messages.duplicateWaypointExists'));
                return false;
            }
            if (destExtWaypointId != null && waypointExtIds.includes(destExtWaypointId)) {
                message.error(t('tms.route.messages.duplicateWaypointExists'));
                return false;
            }
        }
        return true;
    };

    /**
     * 表单统一校验：筛选项 + 途经点
     */
    const validateForm = (): boolean => {
        // 筛选项校验
        if (filters.businessType === undefined) {
            message.error(t('tms.route.messages.pleaseSelectBusinessType'));
            return false;
        }

        if (filters.routeType === undefined) {
            message.error(t('tms.route.messages.pleaseSelectRouteType'));
            return false;
        }

        if (filters.routeType !== TmsRouteTypeEnum.Handover && !routeCode?.trim()) {
            message.error(t('tms.route.messages.pleaseInputRouteCode'));
            return false;
        }

        // carrierSourceType 校验已在交接方需求中废弃

        if (filters.carrierId === undefined) {
            message.error(filters.routeType === TmsRouteTypeEnum.Handover
                ? t('tms.route.messages.pleaseSelectHandoverCarrier')
                : t('tms.route.messages.pleaseSelectCarrier'));
            return false;
        }

        if (filters.status === undefined) {
            message.error(t('tms.route.messages.pleaseSelectStatus'));
            return false;
        }

        // 途经点校验（隐藏时跳过）
        if (showWaypointSection) {
            const hasEmptyStation = tableData.some(row =>
                (row.type === WaypointTypeEnum.Destination || row.type === WaypointTypeEnum.Waypoint) && !row.station
            );
            if (hasEmptyStation) {
                message.error(t('tms.route.messages.pleaseSelectStationForAll'));
                return false;
            }

            const origin = tableData.find(row => row.type === WaypointTypeEnum.Origin);
            const destination = tableData.find(row => row.type === WaypointTypeEnum.Destination);
            if (!origin || !destination) {
                message.error(t('tms.route.messages.originAndDestinationRequired'));
                return false;
            }

            const waypoints = tableData.filter(row => row.type === WaypointTypeEnum.Waypoint);
            if (waypoints.length > WAYPOINT_MAX_COUNT) {
                message.error(t('tms.route.messages.waypointsCannotExceed3'));
                return false;
            }

            if (isExternalStationRoute(filters.businessType, filters.routeType)) {
                for (const row of tableData) {
                    if (row.type === WaypointTypeEnum.Origin) continue;
                    if (resolveExtWaypointIdFromRow(row) == null) {
                        message.error(t('tms.route.messages.invalidWaypoint', { station: row.station || '' }));
                        return false;
                    }
                }
            }
        }

        return true;
    };

    return { validateForm, validateWaypointDuplicates };
};
